import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, CircularProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';

/**
 * Component showing an overall network graph of all wiki entries and their relationships
 */
const OverallRelationshipGraph = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const canvasRef = React.useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [nodePositions, setNodePositions] = useState({});

  useEffect(() => {
    const loadEntries = async () => {
      try {
        const allEntries = await wikiService.getAllWikiEntries();
        setEntries(allEntries);
        
        // Calculate positions using force-directed layout
        calculateNodePositions(allEntries);
      } catch (error) {
        console.error('Error loading entries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const calculateNodePositions = (allEntries) => {
    const width = 1200;
    const height = 900;
    const positions = {};

    // Simple circular layout by type
    const typeGroups = {
      location: allEntries.filter(e => e.type === 'location'),
      person: allEntries.filter(e => e.type === 'person'),
      item: allEntries.filter(e => e.type === 'item'),
      monster: allEntries.filter(e => e.type === 'monster'),
      faction: allEntries.filter(e => e.type === 'faction')
    };

    const centerX = width / 2;
    const centerY = height / 2;
    const typeAngles = {
      location: 0,
      person: Math.PI * 0.4,
      item: Math.PI * 0.8,
      monster: Math.PI * 1.2,
      faction: Math.PI * 1.6
    };
    const typeRadius = 300;

    Object.entries(typeGroups).forEach(([type, typeEntries]) => {
      const baseAngle = typeAngles[type];
      const count = typeEntries.length;
      const spreadRadius = 150;

      typeEntries.forEach((entry, index) => {
        const angle = baseAngle + ((index / Math.max(count, 1)) * Math.PI * 0.35 - Math.PI * 0.175);
        const distance = typeRadius + (Math.random() * spreadRadius - spreadRadius / 2);
        
        positions[entry.id] = {
          x: centerX + distance * Math.cos(angle),
          y: centerY + distance * Math.sin(angle),
          entry: entry
        };
      });
    });

    setNodePositions(positions);
  };

  useEffect(() => {
    if (!canvasRef.current || Object.keys(nodePositions).length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    const getColor = (type) => {
      switch (type) {
        case 'location': return '#42a5f5';
        case 'person': return '#66bb6a';
        case 'item': return '#ffa726';
        case 'monster': return '#e91e63';
        case 'faction': return '#ab47bc';
        default: return '#d98d2a';
      }
    };

    // Draw all relationships first (behind nodes)
    ctx.strokeStyle = 'rgba(176, 141, 87, 0.2)';
    ctx.lineWidth = 1;
    
    Object.values(nodePositions).forEach(node => {
      if (!node.entry.relationships) return;
      
      node.entry.relationships.forEach(rel => {
        const targetNode = nodePositions[rel.entryId];
        if (!targetNode) return;

        ctx.beginPath();
        ctx.moveTo(node.x, node.y);
        ctx.lineTo(targetNode.x, targetNode.y);
        ctx.stroke();
      });
    });

    // Draw nodes
    Object.entries(nodePositions).forEach(([id, node]) => {
      const nodeRadius = 8;
      const isHovered = hoveredNode === id;

      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = isHovered ? getColor(node.entry.type) : wikiTheme.mediumGray;
      ctx.fill();
      ctx.strokeStyle = getColor(node.entry.type);
      ctx.lineWidth = isHovered ? 3 : 2;
      ctx.stroke();

      // Draw label if hovered
      if (isHovered) {
        ctx.fillStyle = wikiTheme.secondary;
        ctx.font = 'bold 12px "Crimson Text", serif';
        ctx.textAlign = 'center';
        ctx.fillText(node.entry.title, node.x, node.y - 15);
      }
    });
  }, [nodePositions, hoveredNode]);

  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Check if any node was clicked
    Object.entries(nodePositions).forEach(([id, node]) => {
      const dist = Math.sqrt((x - node.x) ** 2 + (y - node.y) ** 2);
      if (dist <= 8) {
        navigate(`/wiki/${id}`);
      }
    });
  };

  const handleCanvasMouseMove = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    let foundHover = null;

    Object.entries(nodePositions).forEach(([id, node]) => {
      const dist = Math.sqrt((x - node.x) ** 2 + (y - node.y) ** 2);
      if (dist <= 8) {
        foundHover = id;
      }
    });

    if (foundHover !== hoveredNode) {
      setHoveredNode(foundHover);
      canvas.style.cursor = foundHover ? 'pointer' : 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: wikiTheme.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: wikiTheme.dark, minHeight: '100vh' }}>
      <Button 
        onClick={() => navigate('/wiki')} 
        sx={{ mb: 2, color: wikiTheme.primary, fontFamily: wikiTheme.font }}
      >
        <ArrowBackIcon sx={{ mr: 1 }} />
        Back to Wiki
      </Button>

      <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray }}>
        <Typography variant="h4" sx={{ mb: 2, color: wikiTheme.secondary, fontFamily: wikiTheme.font }}>
          🔗 Overall Relationship Network
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: wikiTheme.tertiary, fontFamily: wikiTheme.font }}>
          Showing all {entries.length} wiki entries and their relationships. Hover over nodes to see names, click to navigate.
        </Typography>

        {/* Legend */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#42a5f5' }} />
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontSize: '0.9rem' }}>
              Locations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#66bb6a' }} />
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontSize: '0.9rem' }}>
              People
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#ffa726' }} />
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontSize: '0.9rem' }}>
              Items
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#e91e63' }} />
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontSize: '0.9rem' }}>
              Monsters
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#ab47bc' }} />
            <Typography sx={{ color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontSize: '0.9rem' }}>
              Factions
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <canvas
            ref={canvasRef}
            width={1200}
            height={900}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            style={{ 
              border: `2px solid ${wikiTheme.lightGray}`,
              borderRadius: '8px',
              backgroundColor: wikiTheme.dark
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default OverallRelationshipGraph;
